import { useEffect, useState } from "react";
import clsx from "clsx";
import {
  ClockIcon,
  KanbanIcon,
  MapPinIcon,
  StarIcon,
  UserIcon,
} from "lucide-react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  JobPipelineCandidateSheet,
  JobPipelineEmptyStateForFilters,
  JobPipelineTableEmptyState,
} from "@/components";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  candidacyStatusToBackgroundColor,
  candidacyStatusToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { locations } from "@/lib/locations";
import { misc } from "@/lib/misc";
import { CandidacyWithProfileResponse, Job } from "@/types";

const columns: ColumnDef<CandidacyWithProfileResponse>[] = [
  {
    id: "name",
    header: () => {
      return (
        <div className="flex items-center gap-1">
          <UserIcon className="h-4 w-4 text-slate-700" />
          <p className="font-semibold text-slate-700">Name</p>
        </div>
      );
    },
    cell: ({ row }) => {
      const { firstName, lastName, desiredPosition } =
        row.original.candidateProfile.profile;

      return (
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-900">
            <p className="text-sm text-white">
              {misc.toTitleCase(firstName?.[0] ?? "")}
              {misc.toTitleCase(lastName?.[0] ?? "")}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-medium text-slate-700">
              {misc.toTitleCase(`${firstName ?? ""} ${lastName ?? ""}`)}
            </p>
            <p className="text-sm text-slate-500">{desiredPosition}</p>
          </div>
        </div>
      );
    },
  },
  {
    id: "location",
    header: () => {
      return (
        <div className="flex items-center gap-1">
          <MapPinIcon className="h-4 w-4 text-slate-700" />
          <p className="font-semibold text-slate-700">Location</p>
        </div>
      );
    },
    cell: ({ row }) => {
      const location = row.original.candidateProfile.profile.location;

      return (
        <p className="font-medium text-slate-700">
          {locations.stripCountrySuffix(location ?? "")}
        </p>
      );
    },
  },
  {
    id: "timeInStage",
    header: () => {
      return (
        <div className="flex items-center gap-1">
          <ClockIcon className="h-4 w-4 text-slate-700" />
          <p className="font-semibold text-slate-700">Time in Stage</p>
        </div>
      );
    },
    cell: ({ row }) => {
      const timeInStage = row.original.timeInStage;

      return <TimeInStage timeInStage={timeInStage} />;
    },
  },
  {
    id: "scoreMatch",
    header: () => {
      return (
        <div className="flex items-center gap-1">
          <StarIcon className="h-4 w-4 text-slate-700" />
          <p className="font-semibold text-slate-700">Score Match</p>
        </div>
      );
    },
    cell: ({ row }) => {
      const score = row.original.candidacyScore;

      return <Score score={score} />;
    },
  },
  {
    id: "stage",
    header: () => {
      return (
        <div className="flex items-center gap-1">
          <KanbanIcon className="h-4 w-4 text-slate-700" />
          <p className="font-semibold text-slate-700">Stage</p>
        </div>
      );
    },
    cell: ({ row }) => {
      const status = row.original.candidacy.status;

      return (
        <Badge className="gap-2 text-sm" variant="outline">
          <div
            className={clsx(
              "h-3 w-3 rounded-full",
              candidacyStatusToBackgroundColor[status],
            )}
          />
          {candidacyStatusToLabel[status]}
        </Badge>
      );
    },
  },
];

const mapColumnIndexToWidth = ({
  index,
  isBody,
}: {
  index: number;
  isBody: boolean;
}) => {
  switch (index) {
    case 0:
      // Slightly wider for the body to account for the scrollbar.
      return isBody ? "36.25%" : "36%";
    default:
      return "15%";
  }
};

const Score = ({ score }: { score: number | null }) => {
  return (
    <div className="flex gap-2">
      {[...Array<number>(score ?? 0)].map((_, i) => (
        <StarIcon key={i} className="h-4 w-4 fill-yellow-500 text-yellow-500" />
      ))}
      {[...Array<number>(5 - (score ?? 0))].map((_, i) => (
        <StarIcon key={i} className="h-4 w-4 fill-slate-200 text-slate-200" />
      ))}
    </div>
  );
};

const TimeInStage = ({ timeInStage }: { timeInStage: string }) => {
  const daysAgo = dates.calculateDaysAgo(timeInStage);
  const timeAgo = dates.timeAgoForPipeline(timeInStage);

  return (
    <p
      className={clsx(
        "font-medium",
        daysAgo < 3
          ? "text-slate-700"
          : daysAgo < 7
            ? "text-yellow-600"
            : "text-red-900",
      )}
    >
      {timeAgo}
    </p>
  );
};

export const JobPipelineTable = ({
  candidaciesWithProfiles,
  isEmptyState,
  job,
}: {
  candidaciesWithProfiles: CandidacyWithProfileResponse[];
  isEmptyState: boolean;
  job: Job;
}) => {
  const [data, setData] = useState(candidaciesWithProfiles);

  const [selectedCandidacyIndex, setSelectedCandidacyIndex] = useState<
    number | null
  >(null);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.candidacy.id, // Row `id` is the candidacy id.
  });

  // `isEmptyState` is for when there are no candidates for the job at all.
  // `isEmptyStateForFilters` is for when there are no candidates using the
  // current filters. It is important to distinguish between the two because
  // we have different UI for each.
  const isEmptyStateForFilters = candidaciesWithProfiles.length === 0;

  useEffect(() => {
    setData(candidaciesWithProfiles);
  }, [candidaciesWithProfiles]);

  return (
    <div className="flex flex-col overflow-auto">
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader
            className={clsx(
              (isEmptyState || isEmptyStateForFilters) &&
                "pointer-events-none opacity-50",
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow className="border-none" key={headerGroup.id}>
                {headerGroup.headers.map((header, i) => {
                  return (
                    <TableHead
                      key={header.id}
                      // Have to do this because Tailwind doesn't support dynamic variables
                      style={{
                        width: mapColumnIndexToWidth({
                          index: i,
                          isBody: false,
                        }),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        {isEmptyState ? (
          <JobPipelineTableEmptyState job={job} />
        ) : isEmptyStateForFilters ? (
          <JobPipelineEmptyStateForFilters />
        ) : (
          <Table>
            <TableBody>
              {table.getRowModel().rows.map((row, index) => (
                <TableRow
                  className="cursor-pointer"
                  key={row.id}
                  onClick={() => {
                    setSelectedCandidacyIndex(index);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      // Have to do this because Tailwind doesn't support dynamic variables
                      style={{
                        width: mapColumnIndexToWidth({
                          index: cell.column.getIndex(),
                          isBody: true,
                        }),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <JobPipelineCandidateSheet
        // TODO: Is there a more efficient way to do this?
        candidaciesWithProfiles={table
          .getRowModel()
          .rows.map((row) => row.original)}
        selectedCandidacyIndex={selectedCandidacyIndex}
        setSelectedCandidacyIndex={setSelectedCandidacyIndex}
      />
    </div>
  );
};
